import {
  Box,
  Paper,
  Popover,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import type {
  HistoryEntry,
  TaskMetadata,
  EquipmentTreeRowFlat,
} from "../../models";

/** Transform function that converts raw API history rows to HistoryEntry[].
 * Each section provides a lambda wrapper casting to its specific API row type. */
type TransformHistoryFn = (
  apiRows: Record<string, unknown>[],
) => HistoryEntry[];
import { useHistoryTableData } from "../../hooks/useHistoryTableData";
import {
  usePartDetailsQuery,
  useTaskMetadataQuery,
  type EqpSection,
  type GetPartDetailsFn,
  type GetTaskMetadataFn,
} from "../../hooks/queries";
import type { PartDetailsParams } from "../../utils/getPartDetailsParams";

// =============================================================================
// Props
// =============================================================================

interface Props {
  section: EqpSection;
  partDetailsParams: PartDetailsParams | null;
  queryFn: GetPartDetailsFn;
  getTaskMetadataFn: GetTaskMetadataFn;
  /** Transforms raw API history rows to HistoryEntry[]. Each section has its own transform. */
  transformHistory: TransformHistoryFn;
  selectedRow: EquipmentTreeRowFlat | null;
  isSpareContainer: (containerId: number | undefined) => boolean;
  /** Header for the PartInfo column. Defaults to EAF format "Part Info (SN | Mk | Md | V)". */
  partInfoHeader?: string;
}

// =============================================================================
// Component
// =============================================================================

export const HistoryPanel = ({
  section,
  partDetailsParams,
  queryFn,
  getTaskMetadataFn,
  transformHistory,
  selectedRow,
  isSpareContainer,
  partInfoHeader,
}: Props) => {
  const theme = useTheme();

  // --- Notes popover state ---
  const [notesPopoverState, setNotesPopoverState] = useState<{
    open: boolean;
    position: { top: number; left: number } | null;
    taskId: number | null;
  }>({ open: false, position: null, taskId: null });

  // --- Data from TanStack Query ---
  const queryResult = usePartDetailsQuery(section, partDetailsParams, queryFn);

  const taskMetadataQuery = useTaskMetadataQuery(
    notesPopoverState.taskId,
    getTaskMetadataFn,
  );

  const rawHistoryRows = queryResult.data?.tables[2]?.rows ?? [];
  const historyEntries = transformHistory(rawHistoryRows);
  const isLoading = queryResult.isLoading;

  const metadata = taskMetadataQuery.data ?? [];
  const isLoadingNotes = taskMetadataQuery.isLoading;

  const handleShowNotes = (
    show: boolean,
    entry?: HistoryEntry,
    position?: { top: number; left: number },
  ) => {
    if (show && entry && position) {
      setNotesPopoverState({
        open: true,
        position,
        taskId: entry.HistoryId,
      });
    } else {
      setNotesPopoverState({ open: false, position: null, taskId: null });
    }
  };

  const { columnDefs } = useHistoryTableData({
    onShowNotes: handleShowNotes,
    partInfoHeader,
  });

  const isSpareContainerSelected =
    selectedRow?.rowType === "sparesContainer" &&
    isSpareContainer(selectedRow?.ContainerId);

  const getTitle = () => {
    if (!selectedRow) return "History";
    if (
      selectedRow.rowType === "container" ||
      selectedRow.rowType === "sparesContainer"
    ) {
      return `History - ${selectedRow.ContainerName ?? ""}`;
    }
    if (selectedRow.rowType === "sparePart") {
      return `History - ${selectedRow.L2PartName ?? ""}`;
    }
    if (selectedRow.rowType === "equipment") {
      return `History - ${selectedRow.EquipmentName}`;
    }
    return `History - ${selectedRow.EquipmentName ?? ""} / ${selectedRow.PositionName ?? ""}`;
  };

  return (
    <Paper
      elevation={2}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {getTitle()}
        </Typography>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
          }}
        >
          {isSpareContainerSelected ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              Please Select a Part
            </Typography>
          ) : (
            <AgGridReact<HistoryEntry>
              rowData={historyEntries}
              columnDefs={columnDefs}
              defaultColDef={{ ...defaultColDef }}
              loadingOverlayComponent={CenteredLazyLoading}
              loading={isLoading}
              {...defaultGridOptions}
            />
          )}
        </Box>
      </Box>

      <Popover
        open={notesPopoverState.open}
        onClose={() => handleShowNotes(false)}
        anchorReference="anchorPosition"
        anchorPosition={notesPopoverState.position ?? undefined}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 0,
              overflow: "hidden",
              maxWidth: 400,
              maxHeight: 300,
            },
          },
        }}
      >
        <Box
          sx={{
            width: 400,
            maxHeight: 300,
            overflowY: "auto",
            p: 1.5,
            backgroundColor: "grey.50",
          }}
        >
          {isLoadingNotes ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : metadata.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No comments
            </Typography>
          ) : (
            metadata.map((entry) => (
              <Box
                key={entry.TaskMetadataId}
                sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  {entry.CreatedBy || "Unknown"},{" "}
                  {entry.CreatedAt
                    ? dxtToLocalServerTime(entry.CreatedAt, "MM/dd/yyyy HH:mm")
                    : "-"}
                  {entry.OperatorNumber != null &&
                    ` (Op: ${entry.OperatorNumber})`}
                </Typography>
                <Typography variant="body2">{entry.Notes}</Typography>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </Paper>
  );
};
